<template>
  <li>
    <component
      :is="item.user.blocked ? 'div' : 'router-link'"
      :to="`/chat/${item.user.userId}`"
      class="flex items-center border-b border-gray-100 pb-3 mb-3 cursor-pointer"
      @click="checkBlock">
      <div
        class="mr-2.5 relative"
        style="min-width: 50px; height: 50px; width: 50px">
        <Avatar
          :class="{ 'grayscale brightness-30': item.user.blocked }"
          :user-item="item.user"
          :show-status="true"/>

        <div
          v-if="item.user.blocked"
          class="absolute inset-0 flex flex-col items-center justify-center text-white">
          <InlineSvg
            :src="require('@/assets/fontawesome/regular/ban.svg')"
            class="w-4 h-4 shrink-0"/>

          <span class="uppercase text-[7px] leading-4 font-bold post-shadow">
            {{ $t('user.blocked') }}
          </span>
        </div>
      </div>

      <div class="flex flex-1 flex-col" style="width: calc(100% - 60px)">
        <div class="flex justify-between">
          <div class="whitespace-nowrap w-8/12">
            <h3
              :title="item.user.name + ` (${item.user.username})`"
              :class="{ 'has-unread': item.numberUnreadMessages > 0 }"
              class="overflow-ellipsis overflow-hidden text-sm font-semibold">
              {{ item.user.name }}

              <InlineSvg
                v-if="options.verificationEnabled && item.user?.tikTokFollowerCount"
                :src="require('@/assets/svg/design/verified.svg')"
                class="inline mb-1 -ml-0.5 mr-0.5"/>

              <span v-if="item.user?.username" class="text-gray-400 inline">@{{ item.user.username }}</span>
            </h3>
          </div>

          <time
            v-if="createdDate"
            :datetime="createdDate"
            :title="createdDateWithTime"
            class="leading-5 text-caption text-gray-600 whitespace-nowrap">
            {{ createdDateFormatted }}
          </time>
        </div>

        <div class="flex justify-between items-center">
          <small
            class="text-sm line-clamp-2"
            :class="{
              'blur-sm': authOptions.messageBlur?.number === message.blurNumber,
              'text-gray-400': !message.text || message.text === '[message deleted]'
            }">
            {{ textMessage }}
          </small>

          <CounterBadge
            v-if="item.numberUnreadMessages"
            :count="item.numberUnreadMessages"/>

          <span
            v-else-if="item.unreadReactionMessage"
            class="bg-brand font-mono text-white rounded-full h-[20px] w-[20px] flex items-center justify-center">
            <InlineSvg
              :src="require('@/assets/fontawesome/solid/dot.svg')"
              class="h-3 w-3"/>
          </span>
        </div>
      </div>
    </component>
  </li>
</template>

<script>

import Avatar from '@/components/Functional/Avatar'
import CounterBadge from '@/components/Functional/CounterBadge'
import { mapState, mapWritableState } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'
import { useDialogsStore } from '@/stores/dialogs.js'
import { openUnblockModal } from '@/helpers/modals.js'
import { userGet } from '@/api/api'

export default {
  name: 'CardListItem',
  components: {
    Avatar,
    CounterBadge
  },
  props: {
    item: {
      type: Object,
      default: () => {},
      required: true
    }
  },
  data () {
    return {}
  },
  computed: {
    ...mapState(useAuthStore, [
      'options',
      'authOptions'
    ]),
    ...mapWritableState(useDialogsStore, [
      'contacts'
    ]),
    message () {
      return this.item.message ? this.item.message : {}
    },
    isAttachmentWithMessage () {
      return this.message.type === 3 && this.message.text && !(['[Photo]', '[Video]'].includes(this.message.text))
    },
    isCurrentItemVideo () {
      return !!this.message.attachment?.fields?.videoUrl || this.message.attachment?.mediaType === 'video'
    },
    isCurrentItemPhoto () {
      return !this.message.attachment?.fields?.videoUrl && this.message.type === 3
    },
    textMessage () {
      const reactionId = this.item?.unreadReactionMessage?.reactionId ?? null

      const emojiById = new Map([
        ['heart', '❤️'],
        ['thumbs_up', '👍'],
        ['fire', '🔥'],
        ['joy', '😂'],
        ['thumbs_down', '👎'],
        ['sob', '😭']
      ])

      const reactionEmoji = emojiById.get(reactionId) ?? null

      if (reactionEmoji && !this.item.numberUnreadMessages) {
        return `Reacted ${reactionEmoji} to "${this.item.unreadReactionMessage.text}"`
      } else if (this.isAttachmentWithMessage) {
        return this.message.text
      } else if (this.isCurrentItemVideo) {
        return '[Video]'
      } else if (this.isCurrentItemPhoto) {
        return '[Photo]'
      } else if (this.message.text) {
        return this.message.text
      } else {
        return 'No messages yet'
      }
    },
    createdDateFormatted () {
      if (!this.message.created) {
        return ''
      }

      const date = this.$dayjs(this.message.created * 1000)
      return date.isToday() ? date.locale(this.$i18n.locale).format('h:mm A') : date.locale(this.$i18n.locale).format('MMM, D YYYY')
    },
    createdDate () {
      if (!this.message.created) {
        return ''
      }

      return this.$dayjs(this.message.created * 1000).locale(this.$i18n.locale).format('YYYY-MM-DD')
    },
    createdDateWithTime () {
      // Nov 28, 2016 14:00
      return this.$dayjs(this.message.created * 1000).locale(this.$i18n.locale).format('MMM, D YYYY HH:mm')
    }
  },
  mounted () {
  },
  methods: {
    openUnblockModal: openUnblockModal,
    async updateUser () {
      try {
        const { data } = await userGet({
          id: this.item.user.userId
        })

        const index = this.contacts.findIndex(c => c.user.userId === data.user.userId)

        if (index !== -1) {
          this.contacts[index].user = data.user
        }
      } catch (error) {
        console.log('Error updating user:', error)
      }
    },
    checkBlock () {
      if (this.item.user.blocked) {
        this.openUnblockModal({ userId: this.item.user.userId, callback: this.updateUser })
      }
    }
  }
}
</script>
